import type { OptionSource } from '@ns/plugin-picker-toolbox-contracts';
import {
  isCreatableOptionSource,
  isEnrichableOptionSource,
} from '@ns/plugin-picker-toolbox-contracts';

import type { ResolvedOption } from './projection';

/**
 * The outcome of turning a selected widget key into a form value. This is the
 * pure decision the picker makes on selection; the component applies it (calls
 * `onChange`, kicks off enrichment). Separating the decision from the effect
 * keeps the rule testable without a DOM (cf-functional-design).
 *
 * - `cleared`: the selection was cleared (`onChange(undefined)`).
 * - `immediate`: a plain or created option; `value` is the FULL value object to
 *   write straight away.
 * - `enrich`: an enrichable source; `provisional` is written first (so the field
 *   is never empty mid-flight) and `item` is the raw item to run
 *   `resolveSelection` on, whose result becomes the final value.
 */
export type SelectionOutcome<TItem, TValue> =
  | { kind: 'cleared' }
  | { kind: 'immediate'; value: TValue }
  | { item: TItem; kind: 'enrich'; provisional: TValue };

/**
 * Decides what a selected key means, given the currently resolved options and the
 * source's capabilities. Pure data function: no IO, no side effects.
 *
 * - A `null` key clears the selection.
 * - A key matching a resolved option yields its FULL value object (BLOCKER 1: the
 *   whole `getOptionValue(item)`, not just the id). If the source is enrichable,
 *   the outcome is `enrich` so the caller runs `resolveSelection` and the enriched
 *   result becomes the value (BLOCKER 4).
 * - A key NOT matching any option, on a creatable source, is turned into an item
 *   via `createOptionFromInput` and its value object is written (BLOCKER 3). On a
 *   non-creatable source a stray key clears the selection.
 */
export function decideSelection<TItem, TValue>(
  source: OptionSource<TItem, TValue>,
  resolvedOptions: readonly ResolvedOption<TItem, TValue>[],
  key: string | null,
): SelectionOutcome<TItem, TValue> {
  if (key === null) {
    return { kind: 'cleared' };
  }

  const chosen = resolvedOptions.find(option => option.id === key);
  if (chosen === undefined) {
    if (isCreatableOptionSource(source)) {
      const created = source.createOptionFromInput(key);
      return { kind: 'immediate', value: source.getOptionValue(created) };
    }
    return { kind: 'cleared' };
  }

  if (isEnrichableOptionSource(source)) {
    return {
      item: chosen.item,
      kind: 'enrich',
      provisional: chosen.optionValue,
    };
  }

  return { kind: 'immediate', value: chosen.optionValue };
}
